<template>
  <div>
    <v-card-title class="body-2 v-card__subtitle">
      <v-icon small class="mr-2">mdi-chart-donut</v-icon>
      Ampelstatus Verteilung
      <v-spacer></v-spacer>
      <v-chip small outlined text>
        <v-icon small left>mdi-cursor-pointer</v-icon>
        Klicken für Details
      </v-chip>
    </v-card-title>
    <v-card-text v-if="!loading && hasData">
      <VueApexCharts
        type="donut"
        :options="chartOptions"
        :series="chartSeries"
        :height="height"
      ></VueApexCharts>
    </v-card-text>
    <v-card-text
      v-else-if="loading"
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-card-text>
    <v-card-text
      v-else
      class="d-flex justify-center align-center"
      :style="{ height: height + 'px' }"
    >
      <div class="text-center grey--text">
        <v-icon large color="grey">mdi-chart-donut</v-icon>
        <div class="mt-2">Keine Daten verfügbar</div>
      </div>
    </v-card-text>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import apexChartsDarkMode from "@/mixins/apexChartsDarkMode";

export default {
  name: "TrafficLightDistributionChart",
  mixins: [apexChartsDarkMode],
  components: {
    VueApexCharts,
  },
  props: {
    trafficLightStats: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          typeof value.green === "number" &&
          typeof value.yellow === "number" &&
          typeof value.red === "number"
        );
      },
    },
    totalCount: {
      type: Number,
      required: true,
    },
    performanceData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 300,
    },
  },
  computed: {
    hasData() {
      return this.totalCount > 0;
    },
    chartSeries() {
      return [
        this.trafficLightStats.green,
        this.trafficLightStats.yellow,
        this.trafficLightStats.red,
      ];
    },
    chartOptions() {
      const vm = this;
      return {
        ...this.apexChartsDarkModeOptions,
        chart: {
          ...this.apexChartsDarkModeOptions.chart,
          type: "donut",
          events: {
            dataPointSelection: function (event, chartContext, config) {
              vm.handleSliceClick(config);
            },
          },
        },
        labels: ["Grün", "Gelb", "Rot"],
        colors: ["#4CAF50", "#FF9800", "#F44336"],
        legend: {
          position: "bottom",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Gesamt",
                  fontSize: "16px",
                  formatter: () => {
                    return this.totalCount;
                  },
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return Math.round(val) + "%";
          },
        },
        noData: {
          text: "Keine Daten verfügbar",
          align: "center",
          verticalAlign: "middle",
          style: {
            fontSize: "14px",
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200,
              },
              legend: {
                position: "bottom",
              },
            },
          },
        ],
      };
    },
  },
  methods: {
    handleSliceClick(config) {
      const { dataPointIndex } = config;
      const statusMap = ["green", "yellow", "red"];
      const status = statusMap[dataPointIndex];
      const labels = [
        "Grün (Erfüllt)",
        "Gelb (Teilweise erfüllt)",
        "Rot (Nicht erfüllt)",
      ];
      const icons = [
        "mdi-check-circle",
        "mdi-alert-circle",
        "mdi-close-circle",
      ];
      const colors = ["success", "warning", "error"];
      const counts = [
        this.trafficLightStats.green,
        this.trafficLightStats.yellow,
        this.trafficLightStats.red,
      ];
      const percentages = [
        this.trafficLightStats.greenPercent,
        this.trafficLightStats.yellowPercent,
        this.trafficLightStats.redPercent,
      ];

      // Get members for this status
      const filteredMembers = this.performanceData
        .filter((member) => member.trafficLightStatus === status)
        .map((member) => {
          let metCount = 0;
          let totalCount = 0;

          if (member.performanceTargetResults) {
            Object.values(member.performanceTargetResults).forEach((result) => {
              if (
                result.status !== "notApplicable" &&
                result.status !== "fullySuspended"
              ) {
                totalCount++;
                if (result.status === "met") metCount++;
              }
            });
          }

          return {
            member: member,
            user: member.user,
            group: member.group,
            summary:
              totalCount > 0
                ? `${metCount} von ${totalCount} erfüllt`
                : "Keine Daten",
          };
        });

      // Group members by user group and sort
      const groupedMembers = {};
      filteredMembers.forEach((item) => {
        const groupKey = item.group.title;
        if (!groupedMembers[groupKey]) {
          groupedMembers[groupKey] = {
            sortKey: item.group.sortKey,
            members: [],
          };
        }
        groupedMembers[groupKey].members.push(item);
      });

      // Sort groups and create member list with headers
      const members = [];
      Object.keys(groupedMembers)
        .sort((a, b) => groupedMembers[a].sortKey - groupedMembers[b].sortKey)
        .forEach((groupTitle) => {
          // Add group header
          members.push({
            groupHeader: groupTitle,
          });
          // Add sorted members
          groupedMembers[groupTitle].members
            .sort((a, b) =>
              a.user.displayName.localeCompare(b.user.displayName)
            )
            .forEach((item) => {
              members.push({
                user: item.user,
                summary: item.summary,
              });
            });
        });

      this.$emit("status-selected", {
        type: "memberList",
        chartContext: {
          label: "Ampelstatus Verteilung",
          icon: "mdi-chart-donut",
        },
        title: labels[dataPointIndex],
        subtitle: `${counts[dataPointIndex]} Mitglieder`,
        icon: icons[dataPointIndex],
        iconColor: colors[dataPointIndex],
        count: counts[dataPointIndex],
        countLabel: "Mitglieder",
        summary: [
          {
            label: "Anzahl",
            value: counts[dataPointIndex],
            color: colors[dataPointIndex],
          },
          {
            label: "Prozentsatz",
            value: `${percentages[dataPointIndex]}%`,
            color: colors[dataPointIndex],
          },
          {
            label: "Gesamt",
            value: this.totalCount,
          },
        ],
        members: members,
      });
    },
  },
};
</script>
